import type { AppCustomRouteRecordRaw } from '@/router/types'
import { TenantConfigApi } from '@/api/lawyer'
import { useUserStoreWithOut } from '@/store/modules/user'

/** 与后端 LawyerTenantConfigService 对齐 */
export const BIZ_MODE_PLATFORM = 'PLATFORM'
export const BIZ_MODE_SELF = 'SELF'

/** 拉律师租户配置所需权限；未开通律师时勿请求，避免全局 Access Denied */
const LAWYER_TENANT_CONFIG_QUERY = 'lawyer:tenant-config:query'
const ALL_PERMISSION = '*:*:*'
const LAWYER_PERM_PREFIX = 'lawyer:'

export function normalizeBizMode(bizMode?: string | null): string {
  const m = String(bizMode || BIZ_MODE_PLATFORM).toUpperCase()
  return m === BIZ_MODE_SELF ? BIZ_MODE_SELF : BIZ_MODE_PLATFORM
}

export function isSelfBizMode(bizMode?: string | null): boolean {
  return normalizeBizMode(bizMode) === BIZ_MODE_SELF
}

/** 当前登录用户是否具备任意律师域权限（含超管）。无则禁止打 /lawyer/** 菜单辅助接口。 */
export function canCallLawyerMenuApis(): boolean {
  const permissions = useUserStoreWithOut().getPermissions
  if (!permissions || permissions.size === 0) {
    return false
  }
  if (permissions.has(ALL_PERMISSION)) {
    return true
  }
  if (permissions.has(LAWYER_TENANT_CONFIG_QUERY)) {
    return true
  }
  for (const p of permissions) {
    if (typeof p === 'string' && p.startsWith(LAWYER_PERM_PREFIX)) {
      return true
    }
  }
  return false
}

function isSettledFirmMenu(route: AppCustomRouteRecordRaw): boolean {
  const path = String(route.path || '')
  if (path === 'settled-firm' || path.endsWith('/settled-firm')) {
    return true
  }
  const component = String((route as any).component || '')
  return component.includes('settled-firm')
}

/**
 * Admin 侧栏按 biz_mode 调整（与 firmErpEnabled 无关）。
 *
 * SELF：入驻律所 → 官方本所（保留入口，禁止多所语义靠页内+API）
 * PLATFORM：保持「入驻律所」
 * 其余律师撮合菜单两模式均显示；律所 ERP 不在此过滤。
 */
export function filterMenusByBizMode(
  routers: AppCustomRouteRecordRaw[],
  bizMode?: string | null
): AppCustomRouteRecordRaw[] {
  if (!routers?.length) return routers
  const self = isSelfBizMode(bizMode)

  return routers.map((route) => {
    const children = route.children?.length
      ? filterMenusByBizMode(route.children, bizMode)
      : route.children

    if (self && isSettledFirmMenu(route)) {
      return {
        ...route,
        name: '官方本所',
        meta: {
          ...(route.meta || {}),
          title: '官方本所'
        },
        children
      }
    }
    return { ...route, children }
  })
}

/**
 * 供全局 generateRoutes 使用：仅在当前用户具备律师域权限时请求接口。
 * 未开通律师的外部租户直接 PLATFORM，避免 /lawyer/tenant-config/get → Access Denied 弹窗。
 */
export async function loadBizModeForMenu(): Promise<string> {
  if (!canCallLawyerMenuApis()) {
    return BIZ_MODE_PLATFORM
  }
  try {
    const cfg = await TenantConfigApi.get()
    return normalizeBizMode(cfg?.bizMode)
  } catch {
    return BIZ_MODE_PLATFORM
  }
}
